/**
 * RoleBulkUploadPanel.jsx — Phase 1.5 Bulk Channel-1 Ingestion.
 *
 * Implements the "Batch Intake Session" mockup verbatim:
 *   • Header: title + role-title subtitle + total count + Start
 *     evaluation CTA (disabled in 1.5 — reserved for Phase 1.6).
 *   • 4 outcome summary cards (Parsed/green, Parse Failed/red,
 *     Duplicates/amber, Pending/blue) with left-coloured rule.
 *   • "Upload Results" table with File / Status pill / Outcome /
 *     Detail columns.
 *   • Footer rule: "Design rule: intake reports operational truth
 *     only — parsed, failed, duplicate, rejected. Evaluation
 *     happens later."
 *
 * STRICT OPERATIONAL HONESTY:
 *   - No scoring. No ranking. No verdict at intake time.
 *   - Every item ends in a terminal pill — no silent abandonment.
 *   - Server-side 60s BackgroundTasks budget surfaces honestly as
 *     `budget_exceeded` pills.
 *   - "Start evaluation" CTA is disabled in 1.5 (Phase 1.6).
 *
 * Out of scope (deferred):
 *   - Bulk evaluation (Phase 1.6).
 *   - PDF export, GDPR erasure end-to-end (Phase 1.7).
 *   - Reviewer assignment.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload, Loader2, Layers, RefreshCw, AlertTriangle, FileText,
} from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { useEmployerLimits } from '@/lib/useEmployerLimits';
import BulkEvalConfirmDialog from './BulkEvalConfirmDialog';

// RC 1.62 Stabilisation Slice (Part D · R-8) — upload ceilings
// are now server-projected via `/api/employer/v1/limits`. The
// constants below are kept ONLY as a documented last-resort
// fallback for the rare boot path that races the limits read;
// the hook fail-LOUDS on transport error so the operator sees
// "Limits unavailable" rather than a silently inflated value.
// Doctrine: NEVER use these constants for active enforcement.
const ACCEPTED_EXT = ['.pdf', '.docx', '.zip'];

// ─── Status / outcome rendering ─────────────────────────────────
const STATUS_PILL = {
  parsed:           { cls: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300', label: 'Parsed' },
  duplicate:        { cls: 'bg-amber-500/10 border-amber-500/40 text-amber-300',       label: 'Duplicate' },
  parse_failed:     { cls: 'bg-rose-500/10 border-rose-500/40 text-rose-300',          label: 'Parse failed' },
  rejected:         { cls: 'bg-rose-500/10 border-rose-400/30 text-rose-300/90',       label: 'Rejected' },
  budget_exceeded:  { cls: 'bg-rose-500/10 border-rose-400/30 text-rose-300/90',       label: 'Budget exceeded' },
  queued:           { cls: 'bg-ndor-surface border-ndor-edge text-ndor-ink-muted',     label: 'Queued' },
  parsing:          { cls: 'bg-blue-500/10 border-blue-500/40 text-blue-300',          label: 'Parsing' },
  evaluating:       { cls: 'bg-blue-500/10 border-blue-500/40 text-blue-300',          label: 'Evaluating' },
  evaluated:        { cls: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300', label: 'Evaluated' },
};

function StatusPill({ status }) {
  const cfg = STATUS_PILL[status] || STATUS_PILL.queued;
  return (
    <Badge
      variant="outline"
      className={`${cfg.cls} text-[10px] uppercase tracking-wider`}
      data-testid={`bulk-status-pill-${status}`}
    >
      {cfg.label}
    </Badge>
  );
}

function outcomeLine(item) {
  switch (item.item_status) {
    case 'parsed':           return 'Candidate created';
    case 'duplicate':        return 'Existing candidate returned';
    case 'parse_failed':     return 'Parser failure';
    case 'rejected':         return 'Rejected at intake';
    case 'budget_exceeded':  return 'Not processed within batch budget';
    case 'parsing':          return 'Parsing…';
    case 'queued':           return 'Queued';
    default:                 return '—';
  }
}

function detailLine(item) {
  if (item.parser_used) return item.parser_used;
  if (item.error_code)  return item.error_code;
  return '—';
}

// ─── Summary card ───────────────────────────────────────────────
function SummaryCard({ rule, label, count, descriptor, testId }) {
  return (
    <div
      className="flex items-stretch rounded-lg border border-ndor-edge bg-ndor-surface overflow-hidden"
      data-testid={testId}
    >
      <div className={`w-1 ${rule}`} />
      <div className="flex-1 p-3">
        <p className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">{label}</p>
        <p className="mt-1 text-2xl font-semibold text-ndor-ink">{count}</p>
        <p className="text-[10px] text-ndor-ink-muted">{descriptor}</p>
      </div>
    </div>
  );
}

// ─── File list / dropzone before upload ─────────────────────────
function PreUploadFileList({ files, onClear, onRemove }) {
  if (!files.length) return null;
  return (
    <div className="space-y-2" data-testid="bulk-pre-upload-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ndor-ink-muted">
          {files.length} file{files.length === 1 ? '' : 's'} ready to upload
        </p>
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] text-ndor-ink-muted hover:text-ndor-ink underline-offset-2 hover:underline"
          data-testid="bulk-pre-upload-clear"
        >
          Clear
        </button>
      </div>
      <ul className="rounded-md border border-ndor-edge divide-y divide-ndor-edge max-h-48 overflow-y-auto">
        {files.map((f, i) => (
          <li key={`${f.name}-${i}`} className="px-3 py-2 flex items-center justify-between gap-3">
            <span className="text-xs text-ndor-ink truncate" title={f.name}>{f.name}</span>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-ndor-ink-muted">
                {(f.size / 1024).toFixed(1)} KB
              </span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="text-[10px] text-ndor-ink-muted hover:text-rose-300"
                data-testid="bulk-pre-upload-remove"
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────
export default function RoleBulkUploadPanel({
  roleId, roleTitle, onTerminalSuccess, onReviewFlaggedIntegrity,
}) {
  // RC 1.62 Stabilisation Slice (Part D) — pull canonical limits
  // from `/api/employer/v1/limits`. When unavailable the upload
  // form gates itself behind a "Limits unavailable" banner. We
  // intentionally do NOT inline FE fallback constants — the
  // server is the single source of truth.
  const { limits, loading: limitsLoading, error: limitsError, refresh: refreshLimits } = useEmployerLimits();
  const PER_FILE_MAX_BYTES = limits?.upload_per_file_max_bytes ?? null;
  const MAX_FILES          = limits?.upload_bulk_max_files ?? null;
  const MAX_BATCH_BYTES    = limits?.upload_bulk_max_batch_bytes ?? null;
  const limitsReady = PER_FILE_MAX_BYTES != null
    && MAX_FILES != null
    && MAX_BATCH_BYTES != null;

  const [pending, setPending]   = useState([]);   // File[] selected by user before submit
  const [busy, setBusy]         = useState(false);
  const [batch, setBatch]       = useState(null); // current batch state from server
  const [items, setItems]       = useState([]);
  const [error, setError]       = useState(null);
  const fileInputRef            = useRef(null);
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const pollRef                 = useRef(null);
  // RC 1.62 Phase B — guard ensures we only fire onTerminalSuccess
  // once per batch (polling can land on the terminal payload twice
  // due to the immediate pollBatch + setInterval race).
  const terminalFiredRef        = useRef(false);

  // ─── WP8 §H.1/§H.2 integrity completion summary ─────────────
  //
  // Once the batch reaches a terminal state with parsed_count > 0
  // we fetch the role's candidate list (WP7 attaches per-row
  // `integrity_classification` sourced from the WP6 classifier)
  // and count how many are unresolved / missing_check. This
  // powers the clean-vs-flagged completion copy variants without
  // introducing a new backend endpoint.
  const [integritySummary, setIntegritySummary] = useState(null);
  const [integritySummaryLoading, setIntegritySummaryLoading] = useState(false);

  const totalsKnown = batch != null;
  const counters = batch ? {
    parsed:           batch.parsed_count          || 0,
    parse_failed:     batch.parse_failed_count    || 0,
    duplicate:        batch.duplicate_count       || 0,
    rejected:         batch.rejected_count        || 0,
    budget_exceeded:  batch.budget_exceeded_count || 0,
    pending:          (batch.total_files || 0)
                       - (batch.parsed_count || 0)
                       - (batch.parse_failed_count || 0)
                       - (batch.duplicate_count || 0)
                       - (batch.rejected_count || 0)
                       - (batch.budget_exceeded_count || 0),
  } : { parsed: 0, parse_failed: 0, duplicate: 0, rejected: 0, budget_exceeded: 0, pending: 0 };

  // ─── Polling ─────────────────────────────────────────────────
  const pollBatch = useCallback(async (batchId) => {
    try {
      const data = await apiFetch(`/api/employer/v1/ingestion/batches/${batchId}`);
      setBatch(data?.batch || null);
      setItems(data?.items || []);
      if (data?.batch && !['queued', 'running'].includes(data.batch.status)) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        // RC 1.62 Phase B — fire the terminal-success callback once
        // per batch, only when at least one dossier was parsed (i.e.
        // there is something worth showing on the Candidates tab).
        // Pure all-duplicate or all-failed batches stay on the
        // Upload tab so the operator can decide.
        const parsedOk = (data.batch.parsed_count || 0) > 0;
        if (parsedOk && !terminalFiredRef.current) {
          terminalFiredRef.current = true;
          if (typeof onTerminalSuccess === 'function') {
            try { onTerminalSuccess(data.batch); } catch { /* never block UI on hand-off */ }
          }
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to refresh batch.');
    }
  }, [onTerminalSuccess]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // WP8 §H.1/§H.2 — after intake completes with parsed rows, count
  // how many of the role's parsed candidates are unresolved / missing
  // their integrity check. Uses WP7's list endpoint which attaches
  // the WP6 classifier state per row. Best-effort: failure leaves
  // the summary null and the panel falls back to the legacy copy.
  useEffect(() => {
    if (!batch || batch.status !== 'complete' || (batch.parsed_count || 0) <= 0) {
      setIntegritySummary(null);
      return undefined;
    }
    let cancelled = false;
    setIntegritySummaryLoading(true);
    (async () => {
      try {
        const parsedIds = new Set(
          (items || [])
            .filter((it) => it.item_status === 'parsed' && it.candidate_id)
            .map((it) => it.candidate_id),
        );
        const data = await apiFetch(
          `/api/employer/v1/roles/${roleId}/candidates`,
        );
        if (cancelled) return;
        const rows = Array.isArray(data?.items) ? data.items : [];
        // Scope to candidates that came out of THIS batch. If no
        // per-item candidate_ids are available (older batch shape),
        // fall back to the full role list for the summary.
        const scoped = parsedIds.size > 0
          ? rows.filter((r) => parsedIds.has(r.id))
          : rows;
        let ready        = 0;
        let unresolved   = 0;
        let missingCheck = 0;
        let excluded     = 0;
        scoped.forEach((r) => {
          const cls = r.integrity_classification;
          if (cls === 'clear' || cls === 'included' || cls === 'grandfathered_legacy') ready += 1;
          else if (cls === 'unresolved') unresolved += 1;
          else if (cls === 'missing_check') missingCheck += 1;
          else if (cls === 'excluded') excluded += 1;
        });
        setIntegritySummary({
          total_processed: scoped.length,
          ready, unresolved, missing_check: missingCheck, excluded,
        });
      } catch {
        if (!cancelled) setIntegritySummary(null);
      } finally {
        if (!cancelled) setIntegritySummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [batch, items, roleId]);

  const startPolling = useCallback((batchId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollBatch(batchId);
    pollRef.current = setInterval(() => pollBatch(batchId), 1500);
  }, [pollBatch]);

  // ─── File picker ─────────────────────────────────────────────
  const onPickFiles = () => fileInputRef.current?.click();

  const validateFile = (f) => {
    const lower = (f.name || '').toLowerCase();
    if (!ACCEPTED_EXT.some((ext) => lower.endsWith(ext))) {
      return 'Only PDF, DOCX, or ZIP files are supported.';
    }
    if (!limitsReady) {
      return 'Server limits unavailable. Refresh and retry.';
    }
    if (f.size > PER_FILE_MAX_BYTES && !lower.endsWith('.zip')) {
      return `${f.name}: max ${Math.floor(PER_FILE_MAX_BYTES / (1024 * 1024))} MB per file.`;
    }
    if (lower.endsWith('.zip') && f.size > MAX_BATCH_BYTES) {
      return `${f.name}: zip exceeds ${Math.floor(MAX_BATCH_BYTES / (1024 * 1024))} MB.`;
    }
    return null;
  };

  const onFilesSelected = (ev) => {
    const chosen = Array.from(ev.target?.files || []);
    ev.target.value = '';
    if (!chosen.length) return;
    // Local validation
    const accepted = [];
    for (const f of chosen) {
      const err = validateFile(f);
      if (err) { toast.error(err); continue; }
      accepted.push(f);
    }
    if (!accepted.length) return;
    // Reject ZIP mixed with regular files
    const hasZip = accepted.some((f) => f.name.toLowerCase().endsWith('.zip'));
    if (hasZip && accepted.length > 1) {
      toast.error('Submit either a single ZIP OR multiple files, not both.');
      return;
    }
    setPending((prev) => {
      const merged = [...prev, ...accepted];
      if (limitsReady && merged.length > MAX_FILES) {
        toast.error(`Max ${MAX_FILES} files per batch. Extras dropped.`);
        return merged.slice(0, MAX_FILES);
      }
      const total = merged.reduce((s, f) => s + f.size, 0);
      if (limitsReady && total > MAX_BATCH_BYTES) {
        toast.error(`Batch exceeds ${Math.floor(MAX_BATCH_BYTES / (1024 * 1024))} MB total.`);
      }
      return merged;
    });
  };

  const onClearPending = () => setPending([]);
  const onRemoveOne = (idx) => setPending((p) => p.filter((_, i) => i !== idx));

  // ─── Submit ──────────────────────────────────────────────────
  const onSubmit = async () => {
    if (!pending.length) return;
    setBusy(true);
    setError(null);
    setBatch(null);
    setItems([]);
    // RC 1.62 Phase B — reset the per-batch terminal-fired guard so
    // a fresh batch within the same session re-fires the callback.
    terminalFiredRef.current = false;
    try {
      const form = new FormData();
      for (const f of pending) form.append('files', f, f.name);
      let body;
      try {
        body = await apiFetch(
          `/api/employer/v1/roles/${roleId}/candidates/upload-bulk`,
          { method: 'POST', body: form },
        );
      } catch (err) {
        const detail  = err?.data?.detail || err?.data || {};
        const code    = detail.code || err?.code || 'UPLOAD_FAILED';
        const message = detail.message || err?.message || 'Bulk upload failed.';
        toast.error(`${code}: ${message}`);
        setError(`${code}: ${message}`);
        return;
      }
      toast.success(`Batch queued · ${body?.total_files || 0} files`);
      setPending([]);
      startPolling(body.batch_id);
    } finally {
      setBusy(false);
    }
  };

  const onRefresh = () => { if (batch?.id) pollBatch(batch.id); };

  // ─── Render ──────────────────────────────────────────────────
  return (
    <Card
      className="border-ndor-edge bg-ndor-surface"
      data-testid="role-bulk-upload-panel"
    >
      <CardContent className="space-y-5 p-5">
        {/* HEADER — mirrors the mockup top-bar */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-semibold text-ndor-ink" data-testid="bulk-header-title">
              Batch Intake Session
            </h3>
            <p className="text-xs text-ndor-ink-muted mt-0.5" data-testid="bulk-header-subtitle">
              {roleTitle ? `${roleTitle} · ` : ''}
              {totalsKnown
                ? `${batch.total_files} CV${batch.total_files === 1 ? '' : 's'} uploaded`
                : 'Drop CVs to begin'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!batch || batch.status !== 'complete' || (counters.parsed === 0)}
            onClick={() => setShowEvalDialog(true)}
            className="text-[11px] uppercase tracking-wider border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 min-h-9 px-4 disabled:opacity-40 disabled:bg-ndor-surface disabled:text-ndor-ink-muted disabled:border-ndor-edge"
            data-testid="bulk-start-evaluation-btn"
            title={batch?.status === 'complete' ? 'Run bulk evaluation on parsed candidates' : 'Available once a batch completes'}
          >
            Start evaluation
          </Button>
        </div>

        {/* SUMMARY CARDS — 4 outcome buckets.
            RC EMPLOYER.WORKFLOW.P0 (2026-02-27) — gate the telemetry
            block on having an in-flight batch. When `batch === null`
            (no session yet), the counters were always zero and read
            as if they described the role's overall dossier list
            below. Doctrinal-honest fix: show telemetry ONLY when a
            batch exists; otherwise surface a calm helper line. */}
        {batch ? (
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-2.5"
          data-testid="bulk-summary-cards"
        >
          <SummaryCard
            rule="bg-emerald-500"
            label="Parsed"
            count={counters.parsed}
            descriptor="ready for evaluation"
            testId="bulk-summary-parsed"
          />
          <SummaryCard
            rule="bg-rose-500"
            label="Parse Failed"
            count={counters.parse_failed}
            descriptor="need re-upload"
            testId="bulk-summary-parse-failed"
          />
          <SummaryCard
            rule="bg-amber-500"
            label="Duplicates"
            count={counters.duplicate}
            descriptor="not recreated"
            testId="bulk-summary-duplicate"
          />
          <SummaryCard
            rule="bg-blue-500"
            label="Pending"
            count={counters.pending + counters.budget_exceeded + counters.rejected}
            descriptor={
              batch?.status === 'budget_exceeded'
                ? `${counters.budget_exceeded} budget-exceeded`
                : batch?.status === 'complete'
                  ? 'intake complete'
                  : 'in flight'
            }
            testId="bulk-summary-pending"
          />
        </div>
        ) : (
          <p
            className="text-[11px] text-ndor-ink-muted text-center py-2"
            data-testid="bulk-summary-cards-placeholder"
          >
            Bulk intake telemetry appears during active upload sessions.
          </p>
        )}

        {/* WP8 §H.1/§H.2 — batch completion integrity summary line.
            The design specifies two variants:
              CLEAN:   "N candidate dossiers processed
                        N ready — no document-integrity indicators detected"
              FLAGGED: "N candidate dossiers processed
                        M ready · K require document review
                        → Review flagged dossiers"
            The Rev 4.4 update also surfaces the missing_check
            category (post-activation candidates with no check row)
            so the operator can distinguish "action required by
            operator" (unresolved) from "action required by ops"
            (missing_check). */}
        {batch && batch.status === 'complete' && integritySummary && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-bg/40 p-3 space-y-1.5"
            data-testid="bulk-completion-integrity-summary"
            data-integrity-ready={integritySummary.ready}
            data-integrity-unresolved={integritySummary.unresolved}
            data-integrity-missing-check={integritySummary.missing_check}
            data-integrity-excluded={integritySummary.excluded}
          >
            <p className="text-xs text-ndor-ink font-medium"
               data-testid="bulk-completion-headline">
              {integritySummary.total_processed} candidate
              {integritySummary.total_processed === 1 ? ' dossier' : ' dossiers'} processed
            </p>
            {integritySummary.unresolved === 0
              && integritySummary.missing_check === 0
              && integritySummary.excluded === 0 ? (
              <p className="text-xs text-emerald-300"
                 data-testid="bulk-completion-clean-line">
                {integritySummary.ready} ready — no document-integrity indicators detected
              </p>
            ) : (
              <div className="text-xs text-ndor-ink-muted space-y-1"
                   data-testid="bulk-completion-flagged-block">
                <p data-testid="bulk-completion-ready-line">
                  <span className="text-emerald-300 font-medium">{integritySummary.ready}</span>
                  {' '}ready for evaluation
                </p>
                {integritySummary.unresolved > 0 && (
                  <p data-testid="bulk-completion-review-line">
                    <span className="text-amber-300 font-medium">{integritySummary.unresolved}</span>
                    {' '}require document review
                  </p>
                )}
                {integritySummary.missing_check > 0 && (
                  <p data-testid="bulk-completion-missing-line">
                    <span className="text-orange-300 font-medium">{integritySummary.missing_check}</span>
                    {' '}missing the required integrity check (ops remediation)
                  </p>
                )}
                {integritySummary.excluded > 0 && (
                  <p data-testid="bulk-completion-excluded-line">
                    <span className="text-rose-300 font-medium">{integritySummary.excluded}</span>
                    {' '}already excluded from evaluation
                  </p>
                )}
                {(integritySummary.unresolved > 0 || integritySummary.missing_check > 0)
                  && typeof onReviewFlaggedIntegrity === 'function' && (
                  <button
                    type="button"
                    onClick={() => onReviewFlaggedIntegrity()}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-200 hover:text-amber-100 underline-offset-2 hover:underline"
                    data-testid="bulk-completion-review-cta"
                  >
                    → Review flagged dossiers
                  </button>
                )}
                <p className="text-[10px] text-ndor-ink-muted pt-1 leading-relaxed">
                  Document review recommended: dossiers with formatting indicators may contain text not normally visible in the submitted documents. Review these dossiers before evaluation.
                </p>
              </div>
            )}
          </div>
        )}
        {batch && batch.status === 'complete' && !integritySummary && integritySummaryLoading && (
          <p className="text-[11px] text-ndor-ink-muted"
             data-testid="bulk-completion-integrity-summary-loading">
            Checking document-integrity indicators…
          </p>
        )}

        {/* PRE-UPLOAD: dropzone + selected file list */}
        {!batch && (
          <div className="space-y-3" data-testid="bulk-pre-upload-zone">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.zip"
              multiple
              className="hidden"
              onChange={onFilesSelected}
              data-testid="bulk-file-input"
            />
            <div className="rounded-lg border border-dashed border-ndor-edge p-4 text-center">
              <Layers className="mx-auto h-5 w-5 text-ndor-ink-muted" />
              <p className="mt-2 text-xs text-ndor-ink">
                Drop PDF, DOCX, or a single ZIP archive.
              </p>
              <p className="text-[10px] text-ndor-ink-muted mt-0.5">
                {limitsReady ? (
                  <>Up to {MAX_FILES} files · max {Math.floor(PER_FILE_MAX_BYTES / (1024 * 1024))} MB/file · {Math.floor(MAX_BATCH_BYTES / (1024 * 1024))} MB total batch.</>
                ) : limitsLoading ? (
                  <>Loading server limits…</>
                ) : (
                  <>Server limits unavailable.</>
                )}
              </p>
              {limitsError ? (
                <p
                  className="text-[10px] text-rose-300 mt-1"
                  data-testid="bulk-limits-error"
                >
                  {limitsError} ·{' '}
                  <button
                    type="button"
                    className="underline hover:text-rose-200"
                    onClick={refreshLimits}
                  >
                    Retry
                  </button>
                </p>
              ) : null}
              <Button
                onClick={onPickFiles}
                size="sm"
                variant="outline"
                disabled={!limitsReady}
                className="mt-3 border-ndor-edge text-ndor-ink disabled:opacity-50"
                data-testid="bulk-file-pick-btn"
              >
                Choose files
              </Button>
            </div>

            <PreUploadFileList
              files={pending}
              onClear={onClearPending}
              onRemove={onRemoveOne}
            />

            {pending.length > 0 && (
              <Button
                onClick={onSubmit}
                disabled={busy}
                size="sm"
                className="w-full bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
                data-testid="bulk-submit-btn"
              >
                {busy ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</>
                ) : (
                  <><Upload className="mr-2 h-4 w-4" />Upload {pending.length} file{pending.length === 1 ? '' : 's'}</>
                )}
              </Button>
            )}
          </div>
        )}

        {/* UPLOAD RESULTS TABLE */}
        {batch && (
          <div className="space-y-3" data-testid="bulk-upload-results">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h4 className="text-sm font-medium text-ndor-ink">Upload Results</h4>
                <p className="text-[10px] text-ndor-ink-muted mt-0.5">
                  Every file becomes an auditable candidate intake record.
                  No scoring is invented during intake.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="text-[10px] uppercase tracking-wider border-ndor-edge text-ndor-ink-muted"
                  data-testid="bulk-batch-status"
                >
                  {batch.status}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefresh}
                  aria-label="Refresh batch"
                  data-testid="bulk-refresh"
                  className="h-7 w-7 p-0"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-ndor-edge overflow-hidden">
              {/* Column headers */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-ndor-bg border-b border-ndor-edge text-[10px] uppercase tracking-wider text-ndor-ink-muted">
                <div className="col-span-5">File</div>
                <div className="col-span-2 text-center">Status</div>
                <div className="col-span-3">Outcome</div>
                <div className="col-span-2 text-right">Detail</div>
              </div>
              {/* Rows */}
              <ul
                className="divide-y divide-ndor-edge max-h-96 overflow-y-auto"
                data-testid="bulk-items-list"
              >
                {items.map((it) => (
                  <li
                    key={it.id}
                    className="grid grid-cols-12 gap-2 items-center px-3 py-2.5"
                    data-testid="bulk-item-row"
                    data-item-status={it.item_status}
                  >
                    <div className="col-span-5 min-w-0">
                      <p
                        className="text-xs text-ndor-ink truncate flex items-center gap-1.5"
                        title={it.original_filename}
                      >
                        <FileText className="h-3.5 w-3.5 text-ndor-ink-muted shrink-0" />
                        {it.original_filename}
                      </p>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <StatusPill status={it.item_status} />
                    </div>
                    <div className="col-span-3 text-[11px] text-ndor-ink-muted truncate">
                      {outcomeLine(it)}
                    </div>
                    <div
                      className="col-span-2 text-[10px] text-ndor-ink-muted text-right truncate"
                      title={it.error_detail || ''}
                    >
                      {detailLine(it)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {error && (
              <div className="text-xs text-rose-400 flex items-start gap-2" data-testid="bulk-error">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* New session reset */}
            <button
              type="button"
              onClick={() => { setBatch(null); setItems([]); setError(null); }}
              className="text-[11px] text-ndor-ink-muted hover:text-ndor-ink underline-offset-2 hover:underline"
              data-testid="bulk-reset"
            >
              Start a new intake session
            </button>
          </div>
        )}

        {/* FOOTER RULE — design rule line from the mockup */}
        <p
          className="text-[10px] text-ndor-ink-muted pt-3 border-t border-ndor-edge leading-relaxed"
          data-testid="bulk-footer-rule"
        >
          Design rule: intake reports operational truth only —
          parsed, failed, duplicate, rejected. Evaluation happens later.
        </p>
      </CardContent>

      <BulkEvalConfirmDialog
        open={showEvalDialog}
        onOpenChange={setShowEvalDialog}
        roleId={roleId}
        candidateIds={(items || []).filter((i) => i.item_status === 'parsed' && i.candidate_id).map((i) => i.candidate_id)}
        onDispatched={() => { /* parent polling already covers status */ }}
        onReviewFlagged={onReviewFlaggedIntegrity}
      />
    </Card>
  );
}
